<script setup lang="ts">
import { ref, computed, watch } from "vue"
import { Button } from "@/components/ui/button"
import { useI18n } from 'vue-i18n';
import { sortDropdownOptions, dropdownSortConfigForColumn } from '@/utils/globals'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu'

const { t } = useI18n();

import type { PropType } from 'vue'

const props = defineProps({
  col: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  selectedOptions: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  dynamicOptions: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  toggleOption: {
    type: Function as PropType<(oid: any, val: any) => void>,
    required: true,
  },
  autoOpen: {
    type: Boolean,
    default: false,
  },
  allowDisabledSelection: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits<{
  (event: 'closed'): void
}>()

const searchQuery = ref('')
const open = ref(false)

watch(open, (isOpen, wasOpen) => {
  if (wasOpen && !isOpen) {
    searchQuery.value = ''
    emit('closed')
  }
})

watch(
  () => props.autoOpen,
  (shouldOpen) => {
    if (shouldOpen) open.value = true
  },
  { immediate: true }
)

// Get the full list of options
const allOptions = computed(() => {
  const rawOptions = props.col.options || props.dynamicOptions[props.col.key] || []
  return sortDropdownOptions(rawOptions, dropdownSortConfigForColumn(props.col, rawOptions))
})

// Only show search if more than 8 entries
const showSearch = computed(() => !!props.col.alwaysShowSearch || allOptions.value.length > 8)

// Computed property dynamically filters options as user types
const filteredOptions = computed(() => {
  if (!searchQuery.value || !showSearch.value) return allOptions.value
  return allOptions.value.filter(opt =>
    String(opt.label).toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

const selectedText = computed(() => Object.values(props.selectedOptions).join(', '))
const buttonText = computed(() => selectedText.value || t('view.all'))

function toggle(opt: any) {
  const selected = !!props.selectedOptions[opt.id]
  if (opt.disabled && !selected && !props.allowDisabledSelection) return
  props.toggleOption(opt.id, !selected)
}
</script>

<template>
<DropdownMenu v-model:open="open">
  <DropdownMenuTrigger asChild>
    <Button
      size="sm"
      variant="outline"
      class="w-full min-w-0 justify-start overflow-hidden"
      :title="selectedText || undefined"
    >
      <span class="block w-full min-w-0 truncate text-left">{{ buttonText }}</span>
    </Button>
  </DropdownMenuTrigger>

  <DropdownMenuContent class="w-60 max-h-64 overflow-y-auto z-[11000]">
    <DropdownMenuLabel>{{ col.label }}</DropdownMenuLabel>

    <!-- Show search only if more than 8 options -->
    <div v-if="showSearch" class="p-2">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Suche..."
        class="w-full px-2 py-1 border rounded focus:outline-none focus:ring focus:ring-blue-300"
        @keydown.stop
        @click.stop
      />
    </div>

    <DropdownMenuGroup>
      <DropdownMenuItem
        v-for="opt in filteredOptions"
        :key="opt.id"
        class="flex items-center gap-2 px-2 py-1 cursor-pointer"
        :style="{ color: opt.color || 'inherit', backgroundColor: opt.bgcolor || 'inherit' }"
        @select.prevent 
        :class="{ 'opacity-50 cursor-not-allowed': opt.disabled && !selectedOptions[opt.id] && !allowDisabledSelection }"
        @click.stop="toggle(opt)"
      >
        <input
          type="checkbox"
          :checked="!!selectedOptions[opt.id]"
          class="accent-blue-500 pointer-events-none"
        />
        <span>{{ opt.label }}</span>
      </DropdownMenuItem>
    </DropdownMenuGroup>
  </DropdownMenuContent>
</DropdownMenu>
</template>
